import { getDocStatusBadgeClassName } from "./docStatus";

const MISSING_VALUE = "N/A";
const DETAIL_MISSING_VALUE = "UNAVAILABLE";
const INTERNAL_RECORD_FIELDS = new Set([
  "_dataset_key",
  "_dataset_name",
  "_record_id",
  "admin_last_edited_at",
  "admin_last_edited_by",
  "admin_last_reviewed_at",
  "admin_last_reviewed_by",
  "changed_fields",
  "created_at",
  "data_version",
  "has_edits",
  "ingested_at",
  "is_edited",
  "latest_version",
  "last_updated_at",
  "raw_api_response",
  "last_updated_by",
  "updated",
  "updated_at",
  "__v",
]);

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const isMeaningfulValue = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed !== "" && trimmed !== MISSING_VALUE;
  }

  if (Array.isArray(value)) {
    return value.some((item) => isMeaningfulValue(item));
  }

  if (isPlainObject(value)) {
    return Object.values(value).some((item) => isMeaningfulValue(item));
  }

  return true;
};

const flattenValue = (value) => {
  if (!isMeaningfulValue(value)) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenValue(item));
  }

  if (isPlainObject(value)) {
    const preferredKeys = ["name", "title", "label", "value", "text", "id"];

    for (const key of preferredKeys) {
      if (isMeaningfulValue(value[key])) {
        return flattenValue(value[key]);
      }
    }

    return Object.entries(value).flatMap(([key, nestedValue]) => {
      const formattedNested = formatDisplayValue(nestedValue, { compact: true });
      return formattedNested && formattedNested !== MISSING_VALUE
        ? [`${startCase(key)}: ${formattedNested}`]
        : [];
    });
  }

  return [String(value)];
};

const getPathSegments = (path) => String(path || "").split(".").filter(Boolean);

const getSourceRoot = (source) => getPathSegments(source)[0];

const resolvePathValue = (source, segments) => {
  if (!segments.length) {
    return source;
  }

  if (Array.isArray(source)) {
    return source.flatMap((item) => resolvePathValue(item, segments));
  }

  if (!isPlainObject(source)) {
    return undefined;
  }

  const [nextSegment, ...remainingSegments] = segments;
  return resolvePathValue(source[nextSegment], remainingSegments);
};

export const getFirstValue = (record, sources = []) => {
  for (const source of sources) {
    const resolvedValue = resolvePathValue(record, getPathSegments(source));
    if (isMeaningfulValue(resolvedValue)) {
      return resolvedValue;
    }
  }

  return null;
};

export const startCase = (value) =>
  String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

export const formatDisplayValue = (value, options = {}) => {
  const { compact = false } = options;

  if (!isMeaningfulValue(value)) {
    return MISSING_VALUE;
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  const flattened = flattenValue(value)
    .map((item) => String(item).trim())
    .filter(Boolean);

  if (!flattened.length) {
    return MISSING_VALUE;
  }

  if (compact) {
    return flattened.join(", ");
  }

  if (flattened.length === 1) {
    return flattened[0];
  }

  return flattened.join("\n");
};

const buildFieldModel = (record, fieldConfig, options = {}) => {
  const { includeMissing = false, missingValue = MISSING_VALUE } = options;
  const effectiveIncludeMissing = fieldConfig.includeMissing ?? includeMissing;
  const effectiveMissingValue = fieldConfig.missingValue ?? missingValue;
  const rawValue = getFirstValue(record, fieldConfig.sources);

  if (!isMeaningfulValue(rawValue)) {
    if (!effectiveIncludeMissing) {
      return null;
    }

    return {
      label: fieldConfig.label,
      kind: fieldConfig.kind || "text",
      className: fieldConfig.className || "",
      value: effectiveMissingValue,
      displayValue: effectiveMissingValue,
      rawValue: null,
      href: "",
      itemKind: fieldConfig.itemKind || "",
    };
  }

  const displayValue = fieldConfig.formatValue
    ? fieldConfig.formatValue(rawValue, record)
    : rawValue;

  if (!isMeaningfulValue(displayValue)) {
    if (!effectiveIncludeMissing) {
      return null;
    }

    return {
      label: fieldConfig.label,
      kind: fieldConfig.kind || "text",
      className: fieldConfig.className || "",
      value: effectiveMissingValue,
      displayValue: effectiveMissingValue,
      rawValue,
      href: "",
      itemKind: fieldConfig.itemKind || "",
    };
  }

  return {
    label: fieldConfig.label,
    kind: fieldConfig.kind || "text",
    className: fieldConfig.className || "",
    value: formatDisplayValue(displayValue),
    displayValue,
    rawValue,
    href: fieldConfig.buildHref ? fieldConfig.buildHref(record, rawValue, displayValue) : "",
    links: fieldConfig.buildLinks ? fieldConfig.buildLinks(record, rawValue, displayValue) : [],
    itemKind: fieldConfig.itemKind || "",
  };
};

const getFieldSources = (fieldConfig) => fieldConfig?.sources || [];

const collectConfiguredSourceRoots = (datasetConfig) => {
  const sourceRoots = new Set();
  const addSources = (sources = []) => {
    sources.map(getSourceRoot).filter(Boolean).forEach((source) => sourceRoots.add(source));
  };

  addSources(getFieldSources(datasetConfig.summary.primary));
  addSources(getFieldSources(datasetConfig.summary.secondary));
  (datasetConfig.summary.columns || []).forEach((fieldConfig) =>
    addSources(getFieldSources(fieldConfig))
  );
  (datasetConfig.detail.meta || []).forEach((fieldConfig) =>
    addSources(getFieldSources(fieldConfig))
  );
  (datasetConfig.detail.sections || []).forEach((section) =>
    (section.fields || []).forEach((fieldConfig) => addSources(getFieldSources(fieldConfig)))
  );
  (datasetConfig.detail.links || []).forEach((link) => addSources(link.sources));

  return sourceRoots;
};

const shouldIncludeRemainingField = (key, value, configuredSourceRoots, section) => {
  const excludedFields = new Set(section.excludeRemainingFields || []);

  return (
    typeof key === "string" &&
    !key.startsWith("_") &&
    !INTERNAL_RECORD_FIELDS.has(key) &&
    !excludedFields.has(key) &&
    !configuredSourceRoots.has(key) &&
    isMeaningfulValue(value)
  );
};

const buildCompanyRelatedRecordsField = (companies) => {
  if (!Array.isArray(companies) || !companies.length) {
    return null;
  }

  const seenCompanyIds = new Set();
  const items = companies
    .map((item) => ({
      recordId: String(item?.company_id || "").trim(),
      label: String(item?.ai_name || "").trim() || String(item?.company_id || "").trim(),
    }))
    .filter((item) => item.recordId && !seenCompanyIds.has(item.recordId) && seenCompanyIds.add(item.recordId));

  if (!items.length) {
    return null;
  }

  return {
    label: "Companies",
    kind: "related-records",
    className: "",
    value: formatDisplayValue(companies),
    displayValue: {
      mode: "records",
      groups: [
        {
          datasetKey: "company",
          datasetLabel: "Companies",
          count: items.length,
          items,
        },
      ],
    },
    rawValue: companies,
    href: "",
    itemKind: "",
  };
};

const buildRemainingFieldModels = (record, datasetConfig, section) => {
  const configuredSourceRoots = collectConfiguredSourceRoots(datasetConfig);

  return Object.entries(record)
    .filter(([key, value]) =>
      shouldIncludeRemainingField(key, value, configuredSourceRoots, section)
    )
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => {
      if (key === "companies") {
        const companiesField = buildCompanyRelatedRecordsField(value);
        if (companiesField) {
          return companiesField;
        }
      }

      return {
        label: startCase(key),
        kind: "text",
        className: "",
        value: formatDisplayValue(value),
        displayValue: value,
        rawValue: value,
        href: "",
        itemKind: "",
      };
    });
};

export const buildSummaryRowModel = (record, datasetConfig) => {
  const primary = buildFieldModel(record, datasetConfig.summary.primary);
  const secondary = buildFieldModel(record, datasetConfig.summary.secondary);
  const columns = datasetConfig.summary.columns.map((fieldConfig) => {
    const fieldModel = buildFieldModel(record, fieldConfig);

    if (fieldModel) {
      return fieldModel;
    }

    return {
      label: fieldConfig.label,
      kind: fieldConfig.kind || "text",
      className: fieldConfig.className || "",
      value: fieldConfig.missingValue || MISSING_VALUE,
      rawValue: null,
    };
  });

  return {
    id: record?._record_id || secondary?.value || primary?.value || "unknown-record",
    primary: primary || {
      label: datasetConfig.summary.primary.label,
      value: "Untitled record",
      kind: "text",
    },
    secondary,
    columns,
  };
};

export const buildDetailModel = (record, datasetConfig) => {
  if (!record) {
    return null;
  }

  const primary = buildFieldModel(record, datasetConfig.summary.primary);
  const secondary = buildFieldModel(record, datasetConfig.summary.secondary);
  const meta = datasetConfig.detail.meta
    .map((fieldConfig) =>
      buildFieldModel(record, fieldConfig, {
        includeMissing: false,
        missingValue: DETAIL_MISSING_VALUE,
      })
    )
    .filter(Boolean);

  const sections = datasetConfig.detail.sections
    .map((section) => {
      const staticFields = (section.fields || [])
        .map((fieldConfig) =>
          buildFieldModel(record, fieldConfig, {
            includeMissing: false,
            missingValue: DETAIL_MISSING_VALUE,
          })
        )
        .filter(Boolean);
      const remainingFields = section.includeRemainingFields
        ? buildRemainingFieldModels(record, datasetConfig, section)
        : [];

      return {
        title: section.title,
        fields: [...staticFields, ...remainingFields],
      };
    })
    .filter((section) => section.fields.length > 0);

  const companyLinksField = buildCompanyRelatedRecordsField(record.companies);
  if (companyLinksField && !sections.some((section) => section.fields.some((field) => field.label === "Companies"))) {
    sections.push({
      title: "Companies",
      fields: [companyLinksField],
    });
  }

  const links = (datasetConfig.detail.links || [])
    .flatMap((link) => {
      const rawValue = getFirstValue(record, link.sources);
      if (link.buildLinks) {
        return link
          .buildLinks(record, rawValue)
          .filter((item) => isMeaningfulValue(item?.text) && item?.href)
          .map((item) => ({
            label: link.label,
            text: item.text,
            href: item.href,
          }));
      }

      const href = link.buildHref(record, rawValue);

      if (!isMeaningfulValue(rawValue) || !href) {
        return [];
      }

      return [{
        label: link.label,
        text: link.valueLabel || formatDisplayValue(rawValue, { compact: true }),
        href,
      }];
    })
    .filter(Boolean);

  return {
    title: primary?.value || "Untitled record",
    subtitle: secondary?.value || "",
    meta,
    sections,
    links,
  };
};

const positiveKeywords = [
  "active",
  "approved",
  "available",
  "complete",
  "equivalent",
  "granted",
  "open",
  "recruiting",
  "sese",
  "success",
];

const cautionKeywords = [
  "hold",
  "pending",
  "review",
  "warn",
];

const negativeKeywords = [
  "denied",
  "recalled",
  "rejected",
  "suspended",
  "terminated",
  "withdrawn",
];

export const getBadgeClassName = (kind, value, theme) => {
  if (kind === "doc-status") {
    return getDocStatusBadgeClassName(value);
  }

  if (kind === "id") {
    return `font-mono text-[11px] ${theme.idBadge}`;
  }

  if (kind === "accent") {
    return theme.accentBadge;
  }

  if (kind === "status") {
    const normalized = String(value || "").toLowerCase();

    if (normalized.includes("unavailable")) {
      return "border border-slate-600 bg-slate-800/70 text-slate-100";
    }

    if (negativeKeywords.some((keyword) => normalized.includes(keyword))) {
      return "border border-rose-400/30 bg-rose-500/10 text-rose-100";
    }

    if (cautionKeywords.some((keyword) => normalized.includes(keyword))) {
      return "border border-amber-400/30 bg-amber-500/10 text-amber-100";
    }

    if (positiveKeywords.some((keyword) => normalized.includes(keyword))) {
      return "border border-emerald-400/30 bg-emerald-500/10 text-emerald-100";
    }

    return "border border-slate-600 bg-slate-800/70 text-slate-100";
  }

  return "border border-slate-600 bg-slate-800/70 text-slate-100";
};

export { normalizeDocStatusValue } from "./docStatus";
